import {
  Drawer,
  Col,
  Row,
  Form,
  Select,
  Button,
  Input,
  Table,
  Space,
  Alert,
  Checkbox,
  message,
  Modal,
} from 'antd';
import { useDispatch } from 'react-redux';
import { closeOrgMemberDrawer } from '../../modules/drawer/actions';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'hooks';
import { Member, Roles, IOrg } from 'modules/orgs/interfaces';
import { removeMember, updateMembers } from 'modules/orgs/actions';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import confirmModal from 'general/confirmModal';
import { isAdmin } from 'components/AdminOnly';
import { useState, useMemo } from 'react';
import { functions } from '../../firebase';

const { Option } = Select;

function MemberDrawer() {
  const context = useAppSelector((state) => state.drawer.orgMember);
  const orgId = useAppSelector((state) => state.orgs.currentOrg.id);
  const members = useAppSelector((state) => state.orgs.currentOrg.members);
  const allOrgs = useAppSelector((state) => state.orgs.orgs);
  const uid = useAppSelector((state) => state.user.details.uid);
  const member = useAppSelector((state) =>
    context?.userId ? state.orgs.currentOrg.members[context?.userId] : null
  );
  const { t } = useTranslation();
  const currentUserIsAdmin = isAdmin(uid);

  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([orgId]);
  const [orgSearchText, setOrgSearchText] = useState<string>('');
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [disableAccount, setDisableAccount] = useState<boolean>(false);

  const dispatch = useDispatch();

  // Filter orgs where this user is a member and admin has access
  const orgsWithMember = useMemo(() => {
    if (!currentUserIsAdmin || !allOrgs || !context?.userId) return [];
    return allOrgs.filter((org: IOrg) => {
      const adminMember = org.members?.[uid];
      const targetMember = org.members?.[context.userId];
      return adminMember !== undefined && targetMember !== undefined;
    });
  }, [currentUserIsAdmin, allOrgs, uid, context?.userId]);

  // Filter orgs based on search text
  const filteredOrgs = useMemo(() => {
    if (!orgSearchText) return orgsWithMember;
    const searchLower = orgSearchText.toLowerCase();
    return orgsWithMember.filter(
      (org: IOrg) =>
        org.title.toLowerCase().includes(searchLower) || org.id.toLowerCase().includes(searchLower)
    );
  }, [orgsWithMember, orgSearchText]);

  const orgTableColumns = [
    {
      title: 'Organization',
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IOrg, b: IOrg) => {
        if (a.title && b.title) {
          return a.title.localeCompare(b.title, 'da', {
            sensitivity: 'base',
            numeric: true,
          });
        }
        return 0;
      },
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
    },
  ];

  const handleOrgSelection = (selectedRowKeys: React.Key[]) => {
    setSelectedOrgIds(selectedRowKeys as string[]);
  };

  const dispatchRemoveMember = async () => {
    if (!context?.userId || !members) return;

    // If admin and multiple orgs selected, use batch delete
    if (currentUserIsAdmin && selectedOrgIds.length > 1) {
      setDeleteLoading(true);
      try {
        const functionRef = functions.httpsCallable('settings');
        const response = await functionRef({
          requestType: 'REMOVE_USER_FROM_ORGS',
          orgIds: selectedOrgIds,
          userId: context.userId,
          disableAccount: disableAccount,
        });

        const accountMsg = disableAccount ? ' and account disabled' : '';
        message.success(
          `User removed from ${selectedOrgIds.length} organization(s)${accountMsg} successfully`
        );
        closeProfile();
      } catch (error: any) {
        console.error('Failed to remove user from organizations:', error);
        message.error(
          error.message || 'Failed to remove user from organizations. Please try again.'
        );
      } finally {
        setDeleteLoading(false);
      }
    } else {
      // Single org delete - use existing action
      dispatch(
        removeMember({
          orgId,
          members: members || {},
          memberId: context.userId,
        })
      );
      closeProfile();
    }
  };

  const closeProfile = () => {
    setSelectedOrgIds([orgId]);
    setOrgSearchText('');
    setDeleteModalVisible(false);
    setDisableAccount(false);
    dispatch(closeOrgMemberDrawer());
  };

  const handleDeleteClick = () => {
    if (currentUserIsAdmin && orgsWithMember.length > 1) {
      // Show modal for multi-org selection
      setDeleteModalVisible(true);
    } else {
      // Single org - show simple confirmation
      confirmModal({
        title: `Er du sikker på du vil fjerne ${member?.name || member?.email}?`,
        onOk: () => dispatchRemoveMember(),
      });
    }
  };
  const dispatchUpdateMembers = (member: Partial<Member>) => {
    if (!member.uid) return;
    // @ts-ignore
    dispatch(updateMembers({ orgId, member: { [member.uid]: member } }));
  };

  const onFinish = (values: any) => {
    if (values.role === Roles.ADMIN) {
      delete values.role;
    }
    delete values.email;
    delete values.prefix;
    delete values.phone;

    dispatchUpdateMembers({ ...member, ...values });

    console.log('Success:', values);
  };

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Select style={{ width: 70 }}>
        <Option value="31">+31</Option>
        <Option value="45">+45</Option>
        <Option value="46">+46</Option>
        <Option value="47">+47</Option>
        <Option value="49">+49</Option>
      </Select>
    </Form.Item>
  );

  return (
    <>
      <Drawer
        key={`${context?.orgId}-${context?.userId}`}
        title={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>{member?.name || member?.email}</div>
            <Button
              danger
              loading={deleteLoading}
              onClick={handleDeleteClick}
              icon={<DeleteOutlined />}
            />
          </div>
        }
        width={720}
        onClose={closeProfile}
        open={Boolean(context?.userId)}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form
              key={`${context?.orgId}-${context?.userId}`}
              name="wrap"
              labelCol={{ flex: '140px' }}
              labelAlign="left"
              labelWrap
              onFinish={onFinish}
              // wrapperCol={{ flex: 1 }}
              colon={false}
              initialValues={{
                name: member?.name,
                email: member?.email,
                role: member?.role,
                phone: member?.phone,
              }}
            >
              <Form.Item label={t('access.table_email')} name="email">
                <Input disabled />
              </Form.Item>
              <Form.Item label={t('access.table_phone')} name="phone">
                <Input disabled addonBefore={prefixSelector} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label={t('access.table_name')} name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>

              <Form.Item label={t('access.table_role')} name="role" rules={[{ required: true }]}>
                <Select placeholder={t('access.add.role_placeholder')}>
                  <Option value="admin" disabled>
                    {t('access.role_admin')}
                  </Option>
                  <Option value="cleaner">{t('access.role_cleaner')}</Option>
                  <Option value="departmentExecutive">{t('access.role_executive')}</Option>
                  <Option value="sales">{t('access.role_sales')}</Option>
                </Select>
              </Form.Item>

              <Form.Item label=" ">
                <Button type="primary" htmlType="submit">
                  {t('access.save')}
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Drawer>

      {/* Multi-org delete modal */}
      <Modal
        title={`Remove ${member?.name || member?.email} from Organizations`}
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedOrgIds([orgId]);
          setOrgSearchText('');
          setDisableAccount(false);
        }}
        onOk={() => {
          setDeleteModalVisible(false);
          dispatchRemoveMember();
        }}
        okText={`Remove from ${selectedOrgIds.length} organization(s)`}
        okButtonProps={{ danger: true, loading: deleteLoading }}
        cancelText="Cancel"
        width={700}
        zIndex={1050}
      >
        <Alert
          message="Select Organizations"
          description={`This user is a member of ${orgsWithMember.length} organizations. Select which ones to remove them from.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Input
          placeholder="Search organizations..."
          prefix={<SearchOutlined />}
          value={orgSearchText}
          onChange={(e) => setOrgSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        <Table
          columns={orgTableColumns}
          dataSource={filteredOrgs}
          rowKey="id"
          pagination={false}
          scroll={{ y: 400 }}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedOrgIds,
            onChange: handleOrgSelection,
            preserveSelectedRowKeys: true,
          }}
        />

        <Alert
          message={`${selectedOrgIds.length} organization(s) selected`}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />

        <div style={{ marginTop: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
          <Checkbox checked={disableAccount} onChange={(e) => setDisableAccount(e.target.checked)}>
            <strong>Also disable user account</strong>
          </Checkbox>
          <div style={{ marginLeft: 24, marginTop: 8, color: '#666', fontSize: '12px' }}>
            This will prevent the user from logging in to any organization. Use this if the user
            should no longer have access to the system at all.
          </div>
        </div>
      </Modal>
    </>
  );
}
export default MemberDrawer;
